/** Parse `#RRGGBB` to RGB channels. */
export function parseHexColor(hex: string): {
	r: number;
	g: number;
	b: number;
} {
	const normalized = hex.trim().replace(/^#/, "");
	if (normalized.length !== 6) {
		throw new Error(`Invalid hex color: ${hex}`);
	}
	const r = Number.parseInt(normalized.slice(0, 2), 16);
	const g = Number.parseInt(normalized.slice(2, 4), 16);
	const b = Number.parseInt(normalized.slice(4, 6), 16);
	if ([r, g, b].some((c) => Number.isNaN(c))) {
		throw new Error(`Invalid hex color: ${hex}`);
	}
	return { r, g, b };
}

export function formatHexColor(r: number, g: number, b: number): string {
	const clamp = (c: number) => Math.min(255, Math.max(0, Math.round(c)));
	const toHex = (c: number) => clamp(c).toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export interface RgbChannelDelta {
	r: number;
	g: number;
	b: number;
}

export function channelDelta(
	from: { r: number; g: number; b: number },
	to: { r: number; g: number; b: number },
): RgbChannelDelta {
	return {
		r: to.r - from.r,
		g: to.g - from.g,
		b: to.b - from.b,
	};
}

export function scaleRgb(hex: string, factor: number): string {
	const { r, g, b } = parseHexColor(hex);
	return formatHexColor(r * factor, g * factor, b * factor);
}

export function addChannelDeltaClamp(
	hex: string,
	delta: RgbChannelDelta,
): string {
	const { r, g, b } = parseHexColor(hex);
	return formatHexColor(r + delta.r, g + delta.g, b + delta.b);
}

export function hexToRgba(hex: string, opacity: number): string {
	const { r, g, b } = parseHexColor(hex);
	return `rgba(${r},${g},${b},${opacity})`;
}
